import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Highlighter, MessageSquarePlus, Trash2, X } from 'lucide-react';
import { applyDomHighlights, clearDomHighlights } from './applyDomHighlights.ts';
import { isTextMarkupType, type SopMarkupAnnotation } from './types.ts';

const NAVY = '#1e40af';

type Props = {
  sectionId: string;
  sectionHeading: string;
  annotations: SopMarkupAnnotation[];
  markupMode: boolean;
  canEdit: boolean;
  onAdd: (item: SopMarkupAnnotation) => void;
  onRemove: (id: string) => void;
  children: React.ReactNode;
};

type MenuState = {
  x: number;
  y: number;
  quote: string;
  placeAbove: boolean;
  selectionBottom: number;
  existingHighlightId: string | null;
  existingComment: SopMarkupAnnotation | null;
};

function newId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0')}`.slice(0, 36);
}

function clampMenuX(x: number): number {
  const pad = 12;
  const max = typeof window !== 'undefined' ? window.innerWidth - pad : 800;
  return Math.min(Math.max(pad, x), max);
}

function normQuote(q: string): string {
  return q.replace(/\s+/g, ' ').trim().toLowerCase();
}

const SopMarkupLayer: React.FC<Props> = ({
  sectionId,
  sectionHeading,
  annotations,
  markupMode,
  canEdit,
  onAdd,
  onRemove,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [commentDraft, setCommentDraft] = useState<string | null>(null);

  const sectionAnns = annotations.filter((a) => a.section_id === sectionId);
  const textComments = sectionAnns.filter((a) => a.type === 'comment');
  const highlightQuotes = sectionAnns
    .filter((a) => a.type === 'highlight' && a.quote)
    .map((a) => a.quote as string);
  const highlightKey = highlightQuotes.join('\0');

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    applyDomHighlights(root, highlightQuotes);
    return () => clearDomHighlights(root);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- highlightKey captures quote list
  }, [highlightKey, children]);

  const closeMenu = useCallback(() => {
    setMenu(null);
    setCommentDraft(null);
  }, []);

  useEffect(() => {
    if (!markupMode || !canEdit) {
      closeMenu();
      return;
    }
    const onMouseUp = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      // Do not treat image-markup interactions as text selection markup.
      if ((e.target as Element | null)?.closest?.('[data-sop-image-markup]')) return;

      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !rootRef.current) return;
      const quote = sel.toString().replace(/\s+/g, ' ').trim();
      if (quote.length < 2 || quote.length > 500) return;
      if (!rootRef.current.contains(sel.anchorNode) || !rootRef.current.contains(sel.focusNode)) return;

      const range = sel.getRangeAt(0);
      const clientRects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0);
      const rect = clientRects[0] ?? range.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;

      const gap = 8;
      const qn = normQuote(quote);
      const anns = annotations.filter((a) => a.section_id === sectionId && isTextMarkupType(a.type));
      const existingHighlight = anns.find((a) => a.type === 'highlight' && normQuote(a.quote || '') === qn) ?? null;
      const existingComment = anns.find((a) => a.type === 'comment' && normQuote(a.quote || '') === qn) ?? null;
      setMenu({
        x: clampMenuX(rect.left + rect.width / 2),
        y: rect.bottom + gap,
        quote,
        placeAbove: false,
        selectionBottom: rect.bottom,
        existingHighlightId: existingHighlight?.id ?? null,
        existingComment,
      });
      setCommentDraft(null);
    };

    const onScrollOrResize = () => closeMenu();

    document.addEventListener('mouseup', onMouseUp);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [markupMode, canEdit, closeMenu, annotations, sectionId]);

  const menuNode =
    menu && markupMode && canEdit
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[100] rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
            style={{
              left: menu.x,
              top: menu.y,
              transform: menu.placeAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
              maxWidth: 'min(20rem, calc(100vw - 24px))',
            }}
            role="dialog"
            aria-label="Add markup"
            onMouseDown={(e) => e.preventDefault()}
          >
            {commentDraft === null ? (
              <div className="flex items-center gap-1">
                {menu.existingHighlightId ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                    onClick={() => {
                      onRemove(menu.existingHighlightId!);
                      window.getSelection()?.removeAllRanges();
                      closeMenu();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove highlight
                  </button>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50"
                    onClick={() => {
                      onAdd({
                        id: newId(),
                        section_id: sectionId,
                        section_heading: sectionHeading,
                        type: 'highlight',
                        quote: menu.quote,
                        color: 'yellow',
                        created_at: new Date().toISOString(),
                      });
                      window.getSelection()?.removeAllRanges();
                      closeMenu();
                    }}
                  >
                    <Highlighter className="h-3.5 w-3.5" /> Highlight
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-50"
                  onClick={() => {
                    setMenu((m) =>
                      m
                        ? {
                            ...m,
                            placeAbove: false,
                            y: Math.min(m.selectionBottom + 8, window.innerHeight - 180),
                          }
                        : m,
                    );
                    setCommentDraft(menu.existingComment?.comment ?? '');
                  }}
                >
                  <MessageSquarePlus className="h-3.5 w-3.5" /> {menu.existingComment ? 'Edit comment' : 'Comment'}
                </button>
                <button type="button" className="rounded p-1 text-gray-400 hover:bg-gray-100" onClick={closeMenu} aria-label="Close">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="w-64 space-y-2">
                <p className="line-clamp-2 text-[11px] text-gray-500">&ldquo;{menu.quote}&rdquo;</p>
                <textarea
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                  rows={3}
                  maxLength={2000}
                  autoFocus
                  placeholder="Add a comment…"
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                />
                <div className="flex justify-end gap-1">
                  <button type="button" className="rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-50" onClick={closeMenu}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!commentDraft.trim()}
                    className="rounded-md px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                    style={{ backgroundColor: NAVY }}
                    onClick={() => {
                      const note = commentDraft.trim();
                      if (!note) return;
                      const existing = menu.existingComment;
                      onAdd({
                        id: existing?.id ?? newId(),
                        section_id: sectionId,
                        section_heading: sectionHeading,
                        type: 'comment',
                        quote: existing?.quote ?? menu.quote,
                        comment: note,
                        color: existing?.color,
                        created_at: existing?.created_at ?? new Date().toISOString(),
                      });
                      window.getSelection()?.removeAllRanges();
                      closeMenu();
                    }}
                  >
                    Save comment
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative" ref={rootRef}>
      {children}

      {textComments.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-sky-100 pt-3">
          <p className="text-[11px] font-semibold tracking-wide text-sky-800">COMMENTS</p>
          {textComments.map((a) => (
              <div key={a.id} className="flex gap-2 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs text-sky-950">
                <div className="min-w-0 flex-1">
                  <p className="mt-0.5 italic text-gray-700">&ldquo;{a.quote}&rdquo;</p>
                  {a.comment ? <p className="mt-1 text-gray-800">{a.comment}</p> : null}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    title="Remove comment"
                    onClick={() => onRemove(a.id)}
                    className="shrink-0 rounded p-1 text-gray-500 hover:bg-white/80 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
        </div>
      )}

      {menuNode}
    </div>
  );
};

export default SopMarkupLayer;
